/**
 * Ranked ladder — the 7-tier euchre card ladder (low → high).
 *
 * Tier is DERIVED from the Glicko-2 rating (never stored independently). See
 * `.docs/plans/euchre-multiplayer-expansion/02-ranked-and-rating.md` §2 and `08` §1.
 * This module is the single frontend source of truth for the tier names, ranges,
 * glyphs, and the pure `ratingToTier()` mapping (unit-tested).
 */

export type RankTier =
  | 'Nine'
  | 'Ten'
  | 'Queen'
  | 'King'
  | 'Ace'
  | 'Left Bower'
  | 'Right Bower'

export interface RankTierDef {
  /** Display name. */
  name: RankTier
  /** Inclusive lower bound of the Glicko-2 rating band. */
  min: number
  /** Inclusive upper bound, or null for the open-ended top tier. */
  max: number | null
  /** Card rank glyph shown on the badge (e.g. "9", "Q", "J"). */
  short: string
  /** Suit glyph for bowers (Left = off-suit ♠, Right = trump ♣); undefined otherwise. */
  suit?: string
  /** Brand color token name for the badge accent. */
  color:
    | 'lake'
    | 'spruce'
    | 'walnut'
    | 'gold'
    | 'barn'
    | 'pine'
    | 'ink'
}

/** The ladder, ordered low → high. Boundaries are inclusive on both ends. */
export const RANK_TIERS: readonly RankTierDef[] = [
  { name: 'Nine', min: 0, max: 899, short: '9', color: 'lake' },
  { name: 'Ten', min: 900, max: 1049, short: '10', color: 'spruce' },
  { name: 'Queen', min: 1050, max: 1199, short: 'Q', color: 'walnut' },
  { name: 'King', min: 1200, max: 1349, short: 'K', color: 'gold' },
  { name: 'Ace', min: 1350, max: 1499, short: 'A', color: 'barn' },
  { name: 'Left Bower', min: 1500, max: 1699, short: 'J', suit: '♠', color: 'pine' },
  { name: 'Right Bower', min: 1700, max: null, short: 'J', suit: '♣', color: 'ink' },
] as const

/** Human-readable rating range for a tier, e.g. "1,050–1,199" or "1,700+". */
export function tierRangeLabel(def: RankTierDef): string {
  const lo = def.min.toLocaleString('en-US')
  if (def.max === null) return `${lo}+`
  return `${lo}–${def.max.toLocaleString('en-US')}`
}

/** Tuple list of [name, rangeLabel] for ladder rendering. */
export const TIER_RANGES: ReadonlyArray<readonly [RankTier, string]> = RANK_TIERS.map(
  (t) => [t.name, tierRangeLabel(t)] as const,
)

/**
 * Map a Glicko-2 rating to its display tier.
 * Ratings below 0 clamp to the lowest tier; everything ≥ the top boundary is the top tier.
 */
export function ratingToTier(rating: number): RankTier {
  for (const tier of RANK_TIERS) {
    if (tier.max === null) return tier.name
    if (rating <= tier.max) return tier.name
  }
  // Unreachable (top tier has max === null) but satisfies the type checker.
  return RANK_TIERS[RANK_TIERS.length - 1].name
}

/** Look up the full tier definition by name. */
export function tierDef(name: RankTier): RankTierDef {
  const found = RANK_TIERS.find((t) => t.name === name)
  // RankTier is a closed union over RANK_TIERS, so this is always defined.
  return found ?? RANK_TIERS[RANK_TIERS.length - 1]
}

/**
 * Progress (0–100) of a rating toward the next tier boundary, plus the points remaining.
 * Returns `null` next-tier info when already in the top tier.
 */
export function tierProgress(rating: number): {
  tier: RankTier
  nextTier: RankTier | null
  nextTierAt: number | null
  pointsToNext: number | null
  percent: number
} {
  const name = ratingToTier(rating)
  const idx = RANK_TIERS.findIndex((t) => t.name === name)
  const def = RANK_TIERS[idx]
  const next = RANK_TIERS[idx + 1]
  if (!next) {
    return { tier: name, nextTier: null, nextTierAt: null, pointsToNext: null, percent: 100 }
  }
  const span = next.min - def.min
  const into = Math.max(0, rating - def.min)
  const percent = span > 0 ? Math.min(100, Math.round((into / span) * 100)) : 0
  return {
    tier: name,
    nextTier: next.name,
    nextTierAt: next.min,
    pointsToNext: Math.max(0, next.min - rating),
    percent,
  }
}
